import { CustomTooltip } from '@/components/Chart/components/CustomTooltip'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts'

import {
  calculateTickIndices,
  createTickFormatter,
  formatYAxisTick,
  getYAxisTicks,
} from './helpers'

export interface ComparisonChartDataItem {
  name: string
  tooltipLabel?: string
  value1: number
  value2?: number
  date: string
  index: number
}

interface ComparisonChartProps {
  data: ComparisonChartDataItem[]
  decimalPlaces?: number
  decimalPlacesSecondary?: number
  showSecondary?: boolean
  primaryLegendLabel?: string
  secondaryLegendLabel?: string
}

export const ComparisonChart = ({
  data,
  decimalPlaces = 2,
  decimalPlacesSecondary = 2,
  showSecondary = false,
  primaryLegendLabel,
  secondaryLegendLabel,
}: ComparisonChartProps) => {
  const firstYear = data.length ? new Date(data[0].date).getFullYear() : null
  const lastYear = data.length ? new Date(data[data.length - 1].date).getFullYear() : null
  const rightPadding = lastYear !== firstYear ? 16 : 10
  const pseudoZeroLineData = data.map((d) => ({
    ...d,
    zero: d.value2 ? 0 : null,
  }))
  const hasNegativeValues = Math.min(...data.map((d) => d.value2 ?? 0)) < 0
  const leftAxis = getYAxisTicks(data, 'value1')
  const rightAxis = showSecondary ? getYAxisTicks(data, 'value2') : leftAxis

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <ComposedChart data={data} margin={{ top: 8, right: 16 }}>
        <CartesianGrid stroke='#eee' strokeDasharray='5 5' />
        <XAxis
          tick={{ fontSize: 11 }}
          dataKey='index'
          type='number'
          domain={[0, data.length - 1]}
          ticks={calculateTickIndices(data)}
          tickFormatter={createTickFormatter(data)}
          padding={{ left: 10, right: rightPadding }}
          interval={0}
        />
        <YAxis
          yAxisId='left'
          width='auto'
          domain={leftAxis.domain}
          ticks={leftAxis.ticks}
          interval={0}
          tickFormatter={(value) => formatYAxisTick(value, leftAxis.ticks)}
          tick={{ fontSize: 11 }}
        />
        {showSecondary && (
          <YAxis
            yAxisId='right'
            orientation='right'
            width='auto'
            domain={rightAxis.domain}
            ticks={rightAxis.ticks}
            interval={0}
            tickFormatter={(value) => formatYAxisTick(value, rightAxis.ticks)}
            tick={{ fontSize: 11 }}
          />
        )}
        {showSecondary && hasNegativeValues && (
          <>
            <ReferenceLine
              yAxisId='right'
              y={0}
              stroke='#666'
              strokeWidth={0.5}
              strokeDasharray='1 1'
            />
            <Line
              data={pseudoZeroLineData}
              dataKey='zero'
              stroke='#8884d8'
              strokeWidth={0}
              dot={{
                r: 1,
                fill: '#8884d8',
              }}
              connectNulls={false}
              yAxisId='right'
            />
          </>
        )}
        <Tooltip
          content={(props: TooltipContentProps<number, string>) => (
            <CustomTooltip
              {...props}
              decimalPlaces={decimalPlaces}
              decimalPlacesSecondary={decimalPlacesSecondary}
            />
          )}
        />
        {showSecondary && (
          <Bar
            yAxisId='right'
            dataKey='value2'
            name={secondaryLegendLabel}
            fill='#8884d8'
            barSize={40}
            isAnimationActive={false}
          />
        )}
        <Line
          yAxisId='left'
          type='monotone'
          dataKey='value1'
          name={primaryLegendLabel}
          stroke='#82ca9d'
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
